import React, { useEffect, useState } from "react";
import "./BranchAnalytics.css";
import Sidebar from "../Sidebar/Sidebar";
import api from "../../api"; // ✅ import your global API client
import { cloudApi } from "../../api";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { FaBox, FaCubes, FaClock, FaExclamationTriangle } from "react-icons/fa";

const BranchAnalytics = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchBranchAnalytics = async () => {
    try {
      setLoading(true);
      // ✅ Use global api client — it handles tokens + refresh
      const res = await cloudApi.get("/users/pharmacies/branch-analytics");
      setBranches(res.data.branches || []);
      setError("");
    } catch (err) {
      console.error("❌ Branch analytics fetch failed:", err);
      setError("❌ Failed to load branch analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="ba-layout">
        <Sidebar />
        <div className="ba-container">
          <h2>🏥 Branch Analytics</h2>
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ba-layout">
      <Sidebar />
      <div className="ba-container">
        <h2>🏥 Branch Analytics</h2>
        {error && <div className="ba-error-box">{error}</div>}

        {branches.length === 0 && (
          <p style={{ textAlign: "center" }}>No branch analytics available.</p>
        )}

        {branches.map((branch) => {
          const topDrugData = branch.top_drug
            ? [branch.top_drug]
            : [{ name: "No Data", total_quantity: 0 }];

          const expiringDrugsData =
            branch.expiring_drugs?.length > 0
              ? branch.expiring_drugs
              : [
                  {
                    name: "No expiring drugs",
                    expiration_date: null,
                    quantity: 0,
                  },
                ];

          const lowStockData =
            branch.low_stock_drugs?.length > 0
              ? branch.low_stock_drugs
              : [{ name: "No low stock drugs", quantity: 0 }];

          return (
            <div key={branch.branch_id} className="ba-card">
              <h3>{branch.branch_name}</h3>

              {/* Stock Summary */}
              <div className="ba-summary">
                <p>
                  <FaBox className="ba-purple" /> Total Drug Entries:{" "}
                  {branch.stock_summary?.total_drug_entries || 0}
                </p>
                <p>
                  <FaCubes className="ba-orange" /> Unique Drugs:{" "}
                  {branch.stock_summary?.unique_drugs || 0}
                </p>
                <p>
                  <FaBox className="ba-blue" /> Total Quantity:{" "}
                  {branch.stock_summary?.total_quantity || 0}
                </p>
              </div>

              {/* Top Drug */}
              <div className="ba-chart-card">
                <h4>⭐ Top Stocked Drug</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={topDrugData}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="total_quantity"
                      fill="#4F46E5"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Expiring Drugs */}
              <div className="ba-chart-card">
                <h4>
                  <FaClock className="ba-red" /> Expiring Drugs
                </h4>
                <table className="ba-table">
                  <thead>
                    <tr>
                      <th>Drug</th>
                      <th>Expiration</th>
                      <th>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expiringDrugsData.map((drug, idx) => (
                      <tr key={idx}>
                        <td>{drug.name}</td>
                        <td>
                          {drug.expiration_date
                            ? new Date(drug.expiration_date).toLocaleDateString()
                            : "-"}
                        </td>
                        <td>{drug.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Low Stock Drugs */}
              <div className="ba-chart-card">
                <h4>
                  <FaExclamationTriangle className="ba-orange" /> Low Stock Alerts
                </h4>
                <ul className="ba-low-stock-list">
                  {lowStockData.map((drug, idx) => (
                    <li key={idx}>
                      {drug.name} — Qty: {drug.quantity}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BranchAnalytics;
